/**
 * The small pieces the Equipment tab and its sheets draw again and again
 * (Boards 5, 6, 18 G/H/I), each at the board's own numbers: the kind tiles,
 * the *Internal* chip and the tinted money line, the grey day chip, the
 * rental's state chip, and a section's head with its count.
 *
 * Colours come from the tokens only — the kind pairs are the
 * `--status-kind-*` variables the token build emits, read the way
 * `StatusChip` reads its own.
 */
import type { ReactNode } from 'react'
import type { RentalState } from '@rowted/shared'
import { cn } from '../lib/cn.js'
import { StatusChip } from '../components/primitives.js'
import { VisitGlyph, type VisitGlyphName } from './glyphs.js'
import { COMMON, RENTALS } from './copy.js'
import { InternalChip } from './internal-chip.js'

/** A kind's own tint — `equipment` indigo (the loader tile), `rental` amber (the truck and the vendor). */
export function kindTint(kind: 'equipment' | 'rental' | 'fee'): { color: string; background: string } {
  return {
    color: `rgb(var(--status-kind-${kind}-fg))`,
    background: `rgb(var(--status-kind-${kind}-bg))`,
  }
}

/**
 * A kind tile: 30 (radius 8, glyph 16 — the type cell, the vendor block),
 * 34 (radius 9, glyph 17 — a rental card's truck).
 */
export function KindTile({ kind, glyph, size }: {
  kind: 'equipment' | 'rental'
  glyph: VisitGlyphName
  size: 30 | 34
}) {
  return (
    <span
      aria-hidden
      className={cn('inline-flex shrink-0 items-center justify-center',
        size === 30 ? 'rounded-button' : 'rounded-tile')}
      style={{ width: size, height: size, ...kindTint(kind) }}
    >
      <VisitGlyph name={glyph} size={size === 30 ? (glyph === 'vendor' ? 15 : 16) : 17} stroke={1.9} />
    </span>
  )
}

/**
 * The tinted money line on a rental card — *Internal · $345.00 a day × 1 ·
 * $345.00* (`surface-internal`, radius 10, 9/12, 12.5px).
 */
export function InternalLine({ text, amount }: { text: string; amount: string }) {
  return (
    <div className="flex items-center gap-2 rounded-card border border-line bg-surface-internal px-3 py-[9px] text-[12.5px]">
      <InternalChip />
      <span className="min-w-0 truncate">{text}</span>
      <span className="flex-1" />
      <b className="shrink-0">{amount}</b>
    </div>
  )
}

/** *Day 3* — 19px, radius 5, 10.5/700 on `line-soft` (Boards 5, 6 A). */
export function DayChip({ n }: { n: number }) {
  return (
    <span className="inline-flex h-[19px] items-center whitespace-nowrap rounded-[5px] bg-line-soft px-1.5 text-[10.5px] font-bold text-text-muted">
      {COMMON.dayN(String(n))}
    </span>
  )
}

/**
 * A vendor rental's chip — §10.2: *To book* warn · *Booked* blue · *Picked up*
 * indigo · *Returned* green · *Cancelled* grey (`semantic.vendorRental`).
 */
const RENTAL_WORDS: Record<RentalState, string> = {
  to_book: RENTALS.state.to_book,
  booked: RENTALS.state.booked,
  picked_up: RENTALS.state.picked_up,
  returned: RENTALS.state.returned,
  cancelled: RENTALS.state.cancelled,
}

export function RentalChip({ state }: { state: RentalState }) {
  return (
    <StatusChip group="vendorRental" status={state} className="shrink-0">
      {RENTAL_WORDS[state]}
    </StatusChip>
  )
}

/** A section's title with its grey count — *Rented from a vendor* `2` (11/600, `line-soft`). */
export function CountPill({ n }: { n: number }) {
  return (
    <span className="rounded-chip bg-line-soft px-[7px] py-px text-[11px] font-semibold text-text-muted">
      {String(n)}
    </span>
  )
}

/**
 * The two section heads' buttons (Board 5): 34px, radius 10, 12.5/600, the
 * plus at 14.5 — **Add equipment** white, **Rent from a vendor** green. On a
 * phone they are 44px (§19.2's touch targets).
 */
export function HeadButton({ primary = false, phone, onClick, children }: {
  primary?: boolean
  phone: boolean
  onClick: () => void
  children: ReactNode
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-button-toolbar border px-3.5',
        'text-[12.5px] font-semibold',
        phone ? 'h-11' : 'h-[34px]',
        primary
          ? 'border-accent bg-accent text-white hover:bg-accent-dark'
          : 'border-line bg-surface text-text hover:bg-hover',
      )}
    >
      <VisitGlyph name="plus" size={14.5} stroke={2} />
      {children}
    </button>
  )
}

/**
 * A small button on a card — *Navigate* (28), *Change* · *Cancel booking* ·
 * *RA-88213.pdf* (30), *Mark picked up* (28): radius 8, 12/600, glyph 14. On a
 * phone, 44.
 */
export function CardButton({ height, phone, glyph, onClick, href, children }: {
  height: 28 | 30
  phone: boolean
  glyph?: VisitGlyphName
  onClick?: () => void
  /** A link out — Navigate's maps address, an agreement file. */
  href?: string
  children: ReactNode
}) {
  const className = cn(
    'inline-flex shrink-0 items-center justify-center gap-1.5 whitespace-nowrap rounded-button border border-line',
    'bg-surface px-3.5 text-[12px] font-semibold text-text hover:bg-hover',
    phone ? 'h-11' : height === 28 ? 'h-7' : 'h-[30px]',
  )
  const inner = (
    <>
      {glyph && <VisitGlyph name={glyph} size={14} stroke={2} />}
      {children}
    </>
  )
  return href
    ? <a href={href} target="_blank" rel="noopener noreferrer" className={className}>{inner}</a>
    : <button type="button" onClick={onClick} className={className}>{inner}</button>
}
